import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Table } from 'app-components';
import { IColumns } from '@components/common/app-components/Table';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { formatFullName } from '@utils/miscellaneous/formatFullName';
import { IEnrollee } from '../types/historicalEnrollment.types';
import {
    GENDER_CODE_MAP,
    GENDER_TRANSLATION_MAP,
    EMPTY_VALUE,
} from '../constants/historicalEnrollment.constants';

// ============================================================================
// Types
// ============================================================================

interface IEnrolleeHistoryTableProps {
    id?: string;
    enrollees: IEnrollee[];
}

// ============================================================================
// Component
// ============================================================================

const EnrolleeHistoryTable: React.FC<IEnrolleeHistoryTableProps> = ({
    id,
    enrollees,
}) => {
    const intl = useIntl();

    const columns: IColumns[] = useMemo(() => [
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.type' }),
            key: 'relationship',
            render: (enrollee: IEnrollee) => (
                <span>{enrollee.relationship}</span>
            ),
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.name' }),
            key: 'name',
            render: (enrollee: IEnrollee) => (
                <span>{formatFullName(enrollee.firstName, enrollee.lastName)}</span>
            ),
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.gender' }),
            key: 'gender',
            render: (enrollee: IEnrollee) => {
                const genderValue = GENDER_CODE_MAP[enrollee.gender];
                const translationKey = genderValue ? GENDER_TRANSLATION_MAP[genderValue] : null;
                return <span>{translationKey ? intl.formatMessage({ id: translationKey }) : EMPTY_VALUE}</span>;
            },
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.dob' }),
            key: 'dob',
            render: (enrollee: IEnrollee) => <span>{enrollee.dob || EMPTY_VALUE}</span>,
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.tobacco' }),
            key: 'tobacco',
            render: (enrollee: IEnrollee) => (
                <span>
                    {intl.formatMessage({ id: enrollee.tobacco ? 'common.yes' : 'common.no' })}
                </span>
            ),
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.ssn' }),
            key: 'ssnLastFour',
            render: (enrollee: IEnrollee) => (
                <span>{socialSecurityNumberTools.maskSsnLastFour(enrollee.ssnLastFour)}</span>
            ),
        },
        {
            titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.enrollee.coverageDates' }),
            key: 'coverageDates',
            render: (enrollee: IEnrollee) => (
                <span>{enrollee.coverageStartDate || EMPTY_VALUE} - {enrollee.coverageEndDate || EMPTY_VALUE}</span>
            ),
        },
        {
            titleId: intl.formatMessage({ id: 'historicalApp.member_ref_id' }),
            key: 'memberId',
            render: (enrollee: IEnrollee) => <span>{enrollee.memberId || EMPTY_VALUE}</span>,
        },
    ], [intl]);

    return (
        <div id={id} data-testid="mp-enrollee-history-table" className="overflow-auto">
            <Table columns={columns} data={enrollees} />
        </div>
    );
};

export default EnrolleeHistoryTable;
